import { getIn } from "formik";
import { useMemo } from "react";

type Params = {
  formik: any;
  fieldText: string;
  fieldNumber?: string;
  currency?: string;
  locale?: string;
  decimals?: number;
};

export const useCurrencyField = ({
  formik,
  fieldText,
  fieldNumber,
  currency = "USD",
  locale = "en-US",
  decimals = 0,
}: Params) => {
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
    [locale, currency, decimals]
  );

  const handleFocus = () => {
    const formatted = getIn(formik.values, fieldText);
    if (!formatted && formatted !== 0) return;

    const numeric = String(formatted).replace(/[^0-9]/g, "");
    if (!numeric) {
      formik.setFieldValue(fieldText, "");
      return;
    }

    const cleaned = decimals > 0 ? numeric.slice(0, -decimals) : numeric;

    formik.setFieldValue(fieldText, cleaned);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // permite input vacío mientras escribe
    if (value === "") {
      formik.setFieldValue(fieldText, "");
      if (fieldNumber) {
        formik.setFieldValue(fieldNumber, 0);
      }
      return;
    }

    // solo números pero sin bloquear escritura
    const raw = value.replace(/[^0-9]/g, "");
    formik.setFieldValue(fieldText, raw);
    if (fieldNumber) {
      const num = Number(raw);
      formik.setFieldValue(fieldNumber, isNaN(num) ? 0 : num);
    }
  };

  const handleBlur = () => {
    const raw = getIn(formik.values, fieldText);

    if (raw === "" || raw == null) {
      formik.setFieldValue(fieldText, "");
      if (fieldNumber) {
        formik.setFieldValue(fieldNumber, 0);
      }
      return;
    }

    const cleanRaw = String(raw).replace(/[^0-9]/g, "");
    if (!cleanRaw) {
      formik.setFieldValue(fieldText, "");
      if (fieldNumber) {
        formik.setFieldValue(fieldNumber, 0);
      }
      return;
    }

    const numberValue = isNaN(Number(cleanRaw)) ? 0 : Number(cleanRaw);
    const formatted = formatter.format(numberValue);

    formik.setFieldValue(fieldText, formatted);

    if (fieldNumber) {
      formik.setFieldValue(fieldNumber, numberValue);
    }
  };

  return {
    value: getIn(formik.values, fieldText) || "",
    handleFocus,
    handleChange,
    handleBlur,
  };
};